/**
 * Vorlage „Teig in die Form füllen“.
 *
 * Ein Gefäß kippt und der Teig fließt in die Form. Er steigt an und bildet
 * dort, wo er auftrifft, einen Hügel. Danach streicht ein Teigschaber die
 * Oberfläche glatt.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { compact, ellipse, faded } from '../scene.ts';
import { pourer, stream } from '../parts/kitchen.ts';
import { arrow, counter, GROUND_Y, shadow } from '../parts/stage.ts';
import { place, spatula } from '../parts/utensils.ts';
import { cakeTin, contents } from '../parts/vessels.ts';
import { booleanValue, defineTemplate } from '../template.ts';

const TIN = { cx: 96, width: 120, height: 26 } as const;
const TIN_RY = TIN.width * 0.16;
const RIM_Y = GROUND_Y - TIN.height - TIN_RY;
/** Ausgussecke des Gefäßes. */
const LIP = { x: 148, y: 44 } as const;
/** Dort trifft der Teig auf. */
const LANDING_X = TIN.cx + 14;

/** Zeitplan. */
const TIME = {
  cupIn: [0.02, 0.08],
  tilt: [0.08, 0.16],
  pour: [0.14, 0.5],
  untilt: [0.48, 0.56],
  cupOut: [0.54, 0.6],
  smooth: [0.6, 0.86],
  reset: [0.9, 1],
} as const;

/** Vorlage „Teig in die Form füllen“. */
export const bakeFillTemplate = defineTemplate({
  id: 'bake.fill',
  group: 'bake',
  title: 'Teig in die Form füllen',
  description:
    'Der Teig wird in die vorbereitete Form gegossen und anschließend mit dem Teigschaber glatt gestrichen.',
  durationSeconds: 6,
  keywords: [
    'in die form',
    'in die springform',
    'form füllen',
    'einfüllen',
    'glatt streichen',
    'glattstreichen',
    'aufs blech',
    'auf das blech',
  ],
  stillTime: 0.4,
  parameters: [{ kind: 'boolean', name: 'smooth', label: 'Glatt streichen', defaultValue: true }],
  inferValues(text) {
    const lower = text.toLowerCase();
    return { smooth: lower.includes('glatt') ? true : undefined };
  },
  scene(values, t) {
    const smoothing = booleanValue(values, 'smooth', true);
    const tin = cakeTin(TIN.cx, RIM_Y, TIN.width, TIN.height);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const filled = ease.inOutSine(segment(t, ...TIME.pour)) * (1 - reset);
    const flow =
      segment(t, TIME.pour[0], TIME.pour[0] + 0.03) * (1 - segment(t, TIME.pour[1] - 0.03, TIME.pour[1]));
    const tilt =
      -70 * (ease.inOutCubic(segment(t, ...TIME.tilt)) - ease.inOutCubic(segment(t, ...TIME.untilt)));
    const cup = ease.outCubic(segment(t, ...TIME.cupIn)) * (1 - segment(t, ...TIME.cupOut));
    const smoothPhase = smoothing ? segment(t, ...TIME.smooth) : 0;
    const flat = ease.inOutSine(smoothPhase) * (1 - reset);
    const tool = smoothing
      ? segment(t, TIME.smooth[0], TIME.smooth[0] + 0.04) *
        (1 - segment(t, TIME.smooth[1] - 0.04, TIME.smooth[1]))
      : 0;

    const level = lerp(0.98, 0.42, filled);
    const surfaceY = RIM_Y + tin.depth * level;
    // Hügel unter dem Strahl, der beim Glattstreichen verschwindet.
    const mound = Math.min(1, filled * 1.6) * (1 - flat);
    const batter =
      filled > 0.01
        ? contents(tin, level, 'deep', [
            mound > 0.02
              ? ellipse(LANDING_X, surfaceY - 1, 26 * mound + 4, 5 * mound + 1, {
                  fill: 'deep',
                  stroke: 'ink',
                  strokeWidth: STROKE.fine,
                })
              : null,
          ])
        : null;
    // Der Schaber fährt zweimal hin und her.
    const sweep = Math.sin(smoothPhase * Math.PI * 4);
    return compact([
      ...counter(),
      shadow(TIN.cx, TIN.width * 0.44),
      ...tin.back,
      batter,
      ...tin.front,
      faded(tool, [place(TIN.cx + sweep * 34, surfaceY - 2, 62, spatula(0.4 * sweep))]),
      flow > 0.02
        ? stream(
            [LIP, { x: LIP.x - 8, y: LIP.y + 26 }, { x: LANDING_X, y: surfaceY - 2 }],
            8 * flow,
            t,
            'deep',
          )
        : null,
      faded(cup, [pourer(LIP, 28, 34, tilt, lerp(0.85, 0.08, segment(t, ...TIME.pour)), 'deep')]),
    ]);
  },
  hints(values) {
    const smoothing = booleanValue(values, 'smooth', true);
    return compact([
      arrow([
        { x: LIP.x + 22, y: LIP.y + 8 },
        { x: LIP.x - 4, y: LIP.y + 40 },
        { x: LANDING_X + 8, y: RIM_Y - 6 },
      ]),
      smoothing
        ? arrow([
            { x: TIN.cx - 40, y: GROUND_Y + 12 },
            { x: TIN.cx + 40, y: GROUND_Y + 12 },
          ])
        : null,
    ]);
  },
});
